import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FreeTextOptions, FreeTextQuestion } from '../../../../interfaces/free-text-question.interface';
import { Question, QuestionResponse } from '../../../../interfaces/questions.interface';
import { Subscription } from 'rxjs/internal/Subscription';
import { CstQuestionnaireService } from '../../../../services/cst-questionnaire.service';
import { CommonQuestionnaireService } from '../../../../services/common-questionnaire.service';
import { AbstractControl, Validators } from '@angular/forms';
import { FormGroupExt } from '../../../../../../form-ext/form-group.ext';
import { FormBuilderExt } from '../../../../../../form-ext/form-builder.ext';
import { CustomerQuestionnaireService } from '../../../../services/customer-questionnaire.service';
import { CifcReview } from '../../../../interfaces/cifc.interface';
import * as _ from 'lodash';

@Component({
    selector: 'app-multiline-text',
    templateUrl: './multiline-text.component.html',
    styleUrls: ['./multiline-text.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class MultilineTextComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
    @Input() question: Question;
    @Input() questionIndex: number;
    @ViewChild('titleTextarea') titleTextarea: ElementRef;
    public questionTitle: string;
    public questionPlaceholder: string;
    public descriptionPlaceholder = $localize`Description (optional)`;
    public answerPlaceholder = $localize`Type placeholder here`;
    public questionBody: FreeTextQuestion;
    public currentAnswer: FreeTextOptions[] = [];
    public questionReview: CifcReview;

    public subscription: Subscription;
    public reviewSubscription: Subscription;
    public questionSubscription: Subscription;
    public questionChangedOnReviewReset: Subscription;
    public multilineForm: FormGroupExt;
    public showErrorSubscription: Subscription;

    constructor(
        public cdr: ChangeDetectorRef,
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public formBuilderExt: FormBuilderExt,
    ) {
        this.subscription = this.customerQuestionnaireService.onQuestionMarked().subscribe((question: Question) => {
            this.questionBody.options = this.currentAnswer;
        });
    }

    public get TitleControl(): AbstractControl | null {
        return this.multilineForm?.group.get('title');
    }

    public get DescriptionControl(): AbstractControl | null {
        return this.multilineForm?.group.get('description');
    }

    public get PlaceholderControl(): AbstractControl | null {
        return this.multilineForm?.group.get('placeholder');
    }

    ngOnInit() {
        this.reviewSubscription = this.commonQuestionnaireService.reviewAddedObs().subscribe((review: CifcReview) => {
            if (review?.question_id === this.question.id && review.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                this.questionReview = review;
            }
        });

        if (this.commonQuestionnaireService.createMode) {
            this.questionSubscription = this.cstQuestionnaireService.getQuestionObs().subscribe((question) => {
                this.cstQuestionnaireService.buildQuestion(this, question);
                this.cdr.detectChanges();
            });
            this.showErrorSubscription = this.cstQuestionnaireService.showErrorOnForm.asObservable().subscribe((val) => {
                this.TitleControl?.markAsTouched();
                this.cdr.detectChanges();
            });
        }

        if (this.commonQuestionnaireService.reviewMode) {
            this.questionChangedOnReviewReset = this.commonQuestionnaireService
                .questionChangedOnReviewResetObs()
                .subscribe((question: Question) => {
                    if (question.id === this.question.id && question.body.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                        this.currentAnswer = question.body.options as FreeTextOptions[];
                        (this.questionReview as any) = _.cloneDeep(
                            this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === this.question.id)?.cifc_review,
                        );
                        if (this.questionReview?.solved) {
                            (this.questionReview as any) = null;
                        }
                    }
                });
        }

        this.multilineForm = this.formBuilderExt.groupExt(
            {
                title: [this.question.title, [Validators.required]],
                description: [this.question.description],
                placeholder: [this.question?.body?.options ? (this.question.body.options as FreeTextOptions[])[0].placeholder : ''],
            },
            {
                updateOn: 'blur',
            },
            {
                title: {
                    required: $localize`Please enter a title for this question`,
                },
            },
        );
        this.cstQuestionnaireService.currentQuestionForm = this.multilineForm;
    }

    ngAfterViewInit(): void {
        if (this.titleTextarea) {
            this.titleTextarea.nativeElement.style.height = this.titleTextarea.nativeElement.scrollHeight + 'px';
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.question && changes.question.currentValue !== changes.question.previousValue) {
            this.question = changes.question.currentValue;
            this.TitleControl?.setValue(this.question.title);
            this.DescriptionControl?.setValue(this.question.description);
            this.PlaceholderControl?.setValue(
                this.question?.body?.options ? (this.question.body.options as FreeTextOptions[])[0].placeholder : '',
            );

            this.cstQuestionnaireService.buildQuestion(this, changes.question.currentValue);
            /**
             * When we add a new multiline text question this is where the "new" question is initialized
             * So we need to reinitialize the form
             */
            this.cstQuestionnaireService.currentQuestionForm = this.multilineForm;

            this.cdr.detectChanges();
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.questionSubscription?.unsubscribe();
        this.reviewSubscription?.unsubscribe();
        this.questionChangedOnReviewReset?.unsubscribe();
        this.showErrorSubscription?.unsubscribe();
    }

    public hasAnswer() {
        return (
            (!!this.currentAnswer[0].answer && this.question.answerChanged) ||
            (this.question.cifc_review && !this.question.cifc_review.solved) ||
            (this.question as unknown as QuestionResponse).body.deleted
        );
    }

    public onTitleChange() {
        this.question.title = this.TitleControl?.value;
    }

    public onDescriptionChange() {
        this.question.description = this.DescriptionControl?.value;
    }

    public onPlaceholderChange() {
        if (this.question?.body?.options) {
            (this.question.body.options as FreeTextOptions[])[0].placeholder = this.PlaceholderControl?.value;
        }
    }

    /* Added this in order to know when a question answer has changed */
    public onAnswerChanged($event: string) {
        this.currentAnswer[0].answer = $event;
        this.question.answerChanged = !!$event;
    }

    public onQuestionMarked($event: boolean) {
        /*Disable ok button after OK button was clicked and answer has not changed */
        this.question.answerChanged = !$event;
    }
}
